/** 組織マスタ(hr:admin)。木の順に並べ、停止・再開・親の付け替えをここから行う。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  TreeSelect,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { OrgUnit, OrgUnitInput } from "../../lib/api";
import { hasFeature } from "../../lib/features";
import { useApi, useMe } from "../../lib/useApi";
import { ORG_UNIT_KINDS } from "./constants";
import { errorText, unitOptions, useHrPerms, useOrgUnits } from "./hooks";

type FormValues = {
  name: string;
  kind: string;
  parent_id?: number | null;
  head_title?: string;
  code?: string;
  sort?: number;
  note?: string;
  gws_ou_path?: string;
};

export function OrgUnitsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { canAdmin } = useHrPerms();
  const me = useMe();
  // GWS の OU 連携は動的グループと同じ門(`hr.groups`)。無ければ列もボタンも出さない
  const gwsOu = hasFeature(me.data, "hr.groups");
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState<OrgUnit | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const query = useOrgUnits(activeOnly);

  // 親の候補は常に全部(停止中も出す。停止中の下には置けないことは API が弾く)
  const all = useQuery({
    queryKey: ["hr", "org-units", false],
    queryFn: () => api.orgUnits(false),
  });

  const open = creating || editing !== null;
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing)
      form.setFieldsValue({
        name: editing.name,
        kind: editing.kind,
        parent_id: editing.parent_id,
        head_title: editing.head_title,
        code: editing.code,
        sort: editing.sort,
        note: editing.note,
        gws_ou_path: editing.gws_ou_path,
      });
  }, [open, editing, form]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };

  const save = useMutation({
    mutationFn: (body: OrgUnitInput) =>
      editing ? api.updateOrgUnit(editing.id, body) : api.createOrgUnit(body),
    onSuccess: () => {
      void message.success(editing ? "更新した" : "作成した");
      setCreating(false);
      setEditing(null);
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  /** GWS の OU を組織マスタに合わせる作業を 1 件起票する。作るだけで消さない。 */
  const syncOu = useMutation({
    mutationFn: () => api.syncOrgUnitsToGws(),
    onSuccess: (r) => {
      void message.success(
        r.mode === "auto"
          ? `同期の依頼を出した(対象 ${r.units} 件。タスク #${r.task_id})`
          : `手順のタスクを起票した(対象 ${r.units} 件。タスク #${r.task_id})`,
      );
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.updateOrgUnit(id, { active }),
    onSuccess: (row) => {
      void message.success(row.active ? "再開した" : "停止した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<OrgUnit> = [
    {
      title: "組織",
      width: 360,
      dataIndex: "name",
      render: (v: string, row) => (
        <span style={{ paddingLeft: row.depth * 18 }}>
          {row.depth > 0 && <Typography.Text type="secondary">└ </Typography.Text>}
          {v}
        </span>
      ),
    },
    { title: "種類", dataIndex: "kind_label", width: 160 },
    { title: "責任者", dataIndex: "head_name", width: 280,
      render: (v: string, row) => (v ? `${row.head_title_label}: ${v}` : "") },
    { title: "所属", dataIndex: "member_count", width: 160 },
    { title: "子", dataIndex: "child_count", width: 90 },
    { title: "コード", dataIndex: "code", width: 120 },
    ...(gwsOu
      ? [
          {
            title: "GWS の OU",
            ellipsis: true,
            onCell: (row: OrgUnit) => ({ title: row.gws_ou_path || "(連携しない)" }),
            dataIndex: "gws_ou_path",
            width: 400,
            render: (v: string, row: OrgUnit) =>
              v ? (
                <Space size={4} wrap>
                  <span>{v}</span>
                  {row.gws_ou_id ? <Tag color="green">同期済</Tag> : <Tag>未同期</Tag>}
                </Space>
              ) : (
                <Typography.Text type="secondary">(連携しない)</Typography.Text>
              ),
          },
        ]
      : []),
    {
      title: "状態",
      dataIndex: "active",
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">稼働中</Tag> : <Tag>停止中</Tag>),
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 160,
            render: (_: unknown, row: OrgUnit) => (
              <Space>
                <Button size="small" onClick={() => setEditing(row)}>
                  編集
                </Button>
                <Switch
                  size="small"
                  checked={row.active}
                  loading={toggle.isPending}
                  onChange={(v) => toggle.mutate({ id: row.id, active: v })}
                />
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          組織マスタ
        </Typography.Title>
        <Space>
          <Checkbox checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)}>
            稼働中だけ
          </Checkbox>
          {canAdmin && gwsOu && (
            <Popconfirm
              title="GWS OU を同期"
              description="組織マスタに合わせて GWS の OU を作る・名前を変える・移す。消すことはしない"
              okText="起票する"
              cancelText="やめる"
              onConfirm={() => syncOu.mutate()}
            >
              <Button loading={syncOu.isPending}>GWS OU を同期</Button>
            </Popconfirm>
          )}
          {canAdmin && (
            <Button type="primary" onClick={() => setCreating(true)}>
              組織を作る
            </Button>
          )}
        </Space>
      </Flex>

      {!canAdmin && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="閲覧だけできる"
          description="組織の作成・変更は hr:admin だけ。"
        />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="組織を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<OrgUnit>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
      />

      <Modal
        open={open}
        title={editing ? `組織の編集(${editing.name})` : "組織を作る"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={save.isPending}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onOk={() => {
          void form.validateFields().then((v) =>
            save.mutate({
              name: v.name,
              kind: v.kind,
              parent_id: v.parent_id ?? 0,
              head_title: v.head_title ?? "",
              code: v.code ?? "",
              sort: v.sort ?? 0,
              note: v.note ?? "",
              ...(gwsOu ? { gws_ou_path: v.gws_ou_path ?? "" } : {}),
            }),
          );
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="名前" rules={[{ required: true, message: "名前は必須" }]}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item
            name="parent_id"
            label="親組織"
            extra="未選択なら会社(組織図の根)として作る"
          >
            <TreeSelect
              allowClear
              treeDefaultExpandAll
              treeData={unitOptions(all.data?.items ?? [])}
            />
          </Form.Item>
          <Form.Item name="kind" label="種類" initialValue="department">
            <Select options={ORG_UNIT_KINDS} />
          </Form.Item>
          <Form.Item name="head_title" label="責任者の肩書" extra="空なら種類ごとの既定(部→部長)">
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item name="code" label="コード">
            <Input maxLength={40} />
          </Form.Item>
          <Form.Item name="sort" label="並び順">
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label="備考">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          {gwsOu && (
            <Form.Item
              name="gws_ou_path"
              label="GWS の OU"
              extra="「/」から始まる経路(例 /PoC/営業本部)。空ならこの組織は OU 連携の対象外で、配下の人は最も近い祖先の OU に置く"
            >
              <Input maxLength={255} placeholder="/PoC/営業本部" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
